import { useToast } from "@/components/podkit/toasts/use-toast";
import { useDeleteAccount, type PlainAccount } from "@/queries/account-queries";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { TrackLocations } from "@/hooks/use-segment";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { formatError } from "@/utils/errors";
import { ErrorMessage } from "@/components/ErrorMessage";

export const DeleteAccountModal: FC<{ account: PlainAccount; open: boolean; onClose: () => void }> = ({
    account,
    open,
    onClose,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteAccount = useDeleteAccount();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            deleteAccount.reset();
            onClose();
        }
    };

    const handleDeleteAccount = useCallback(async () => {
        try {
            await deleteAccount.mutateAsync({ accountId: account.id });
            navigate("/account-deleted");
        } catch (error) {
            toast({ title: "Failed to delete account", description: formatError(error) });
        }
        return;
    }, [account.id, deleteAccount, navigate, toast]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.DeleteAccountModal}
                data-testid="delete-account-modal"
            >
                <DialogHeader>
                    <DialogTitle>Delete account</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <ErrorMessage error={deleteAccount.error} data-testid="account-delete-failure-message" />
                    <div className="flex flex-col gap-2 rounded-xl border-[0.5px] px-6 py-3 text-base text-content-primary">
                        <Text>
                            Deleting your Gitpod account is permanent and irreversible. <br />
                            This action will:
                        </Text>
                        <ul className="pl-6 *:list-item *:list-disc">
                            <ul>Remove your access to all projects and environments</ul>
                            <ul>Remove your account from all organizations</ul>
                            <ul>Revoke access to any runners (we recommend deleting them beforehand)</ul>
                            <ul>Remove all authentication tokens, access keys, and personal preferences</ul>
                        </ul>
                    </div>
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <DialogClose asChild>
                        <Button type="button" variant="secondary" onClick={onClose} autoFocus={true}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        loading={deleteAccount.isPending}
                        onClick={handleDeleteAccount}
                        data-testid="delete-account-confirm-btn"
                    >
                        Delete Account
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
